import { Injectable } from '@angular/core';
import { ANALYTICS_EVENTS } from '../data-models/constants/analytics-extended.constant';
import {
  ACCOUNT_RECOVERY_PAGES,
  EVENT_CLICK_TYPE,
  USER_LOGGED_IN_STATUS
} from '../data-models/constants/analytics.constant';
import {
  AnalyticsSiteAssets,
  AnalyticsUser,
  ClickEventAnalytics
} from './analytics.interfaces';
import { ContentBodyAbodeExtendedAnalytics } from './content-body-extended-analytics.service';
import { getHeroParent, getNonHeroParent } from './content-body-helper-utils';

@Injectable({
  providedIn: 'root'
})
export class ContentBodyExtendedHelperAnalytics extends ContentBodyAbodeExtendedAnalytics {
  setHomePageSiteAssetsAnalytics(
    cardDiv: Element,
    eventDetail: string,
    customPosition?: string
  ): AnalyticsSiteAssets[] {
    if (!cardDiv) return [];
    const relevantElements = this.getRelevantElements(cardDiv);
    const component_type = this.getComponentName(eventDetail) || '';
    return this.mapToSiteAssets(
      relevantElements,
      component_type,
      customPosition
    );
  }

  private getRelevantElements(cardDiv: Element): Element[] {
    return Array.from(cardDiv.querySelectorAll('h3, span, a')).filter(
      (element) =>
        element.textContent?.trim() &&
        ['card-headertxt', 'card-title brand-head-home'].includes(
          element.className
        )
    );
  }

  private mapToSiteAssets(
    elements: Element[],
    type: string,
    customPosition?: string
  ): AnalyticsSiteAssets[] {
    const customIndex = this.getCustomIndex(customPosition, type);
    return elements.map((element, index) => ({
      name: element.textContent?.trim() || '',
      type,
      position: customPosition || `${index + 1} of ${elements.length}`,
      id: this.computePageLoadAssetID(type, customIndex ?? index)
    }));
  }

  private getCustomIndex(
    customPosition?: string,
    type?: string
  ): number | null {
    return customPosition && type === 'banner'
      ? parseInt(customPosition.split(' ')[0]) - 1
      : null;
  }

  setLoginGlobalStatus(
    analyticsUser: AnalyticsUser,
    loginPage: string,
    loginMethod: string
  ): void {
    const loggedIn = analyticsUser.attributes.is_logged_in;
    this.loginMethodUtil = loginMethod;
    const status = this.getLoggedInStatus(loggedIn);
    const eventDetail = `${ACCOUNT_RECOVERY_PAGES.GLOBAL_HEADER}:${status}`;
    const data: ClickEventAnalytics = {
      event: ANALYTICS_EVENTS.CLICK,
      event_detail:
        eventDetail === USER_LOGGED_IN_STATUS.LOGIN_FAILURE
          ? 'log-in-failure'
          : eventDetail,
      click: {
        name: this.getLoggedInStatus(loggedIn),
        type: EVENT_CLICK_TYPE.BUTTON,
        page_section: loginPage,
        action: this.getLoggedInStatus(loggedIn)
      },
      user: { ...analyticsUser },
      ...(eventDetail === USER_LOGGED_IN_STATUS.LOGIN_FAILURE && {
        error: {
          code: 'LOGIN_FAILED',
          message: 'Login authentication failed'
        }
      })
    };
    this.analyticsService.setAnalytics(data);
  }

  getLoggedInStatus(loggedInStatus: string): string {
    return loggedInStatus === USER_LOGGED_IN_STATUS.AUTHENTICATED
      ? USER_LOGGED_IN_STATUS.LOGIN_SUCCESS
      : USER_LOGGED_IN_STATUS.LOGIN_FAILURE;
  }

  getContentInfo(clickedElement: HTMLElement): {
    clicked: string;
    parent?: string;
  } {
    const clickedText = clickedElement.textContent?.trim() || '';
    return (
      getHeroParent(clickedElement, clickedText) ||
      getNonHeroParent(clickedElement, clickedText)
    );
  }
}
